import classNames from 'classnames';
import * as React from 'react';
import type { ReactNode } from 'react';
import { FormLabel } from '../../FormLabel/FormLabel';
import type { FormFieldProps } from '../FormFieldProps';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { Theme } from '~/Utils/Theme';

export interface FormFieldTypeInputTextProps
    extends FormFieldProps<string>,
        Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof FormFieldProps<string>> {
    label: ReactNode | ((asterisk: ReactNode) => ReactNode);
    className?: string;
    ariaDescribedBy?: string;
    theme?: Theme;
    showInlineError?: boolean;
    isDisabled?: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
}

export const FormFieldTypeInputText: React.FC<React.PropsWithChildren<FormFieldTypeInputTextProps>> = React.memo(
    props => {
        const contextualTheme = useTheme();

        const {
            label,
            errorMessage,
            showErrorMessage = false,
            showInlineError = true,
            className,
            required,
            type = 'text',
            ariaDescribedBy,
            theme = contextualTheme,
            isDisabled,
            inputRef,
            ...inputProps // name, onChange, onBlur, value
        } = props;

        const { withRoundedCorners } = theme;

        const renderErrorMessage = () => (
            <span className={showInlineError ? 'Form__errorMessage' : 'visuallyHidden'}>{errorMessage}</span>
        );

        return (
            <div
                className={classNames(
                    'Form__field',
                    'Form__fieldText',
                    className,
                    isDisabled && 'Form__fieldDisabled',
                    showErrorMessage && 'Form__fieldError',
                    withRoundedCorners && 'Form__fieldRounded'
                )}
            >
                <label>
                    <div className="Form__label">
                        <FormLabel label={label} required={required} />
                    </div>
                    <input
                        type={type}
                        required={required}
                        disabled={isDisabled}
                        {...inputProps}
                        onChange={e => inputProps.onChange(e.target.value)}
                        onBlur={e => {
                            inputProps.onBlur && inputProps.onBlur(e);
                            inputProps.value && inputProps.onChange(inputProps.value.trim());
                        }}
                        aria-describedby={ariaDescribedBy}
                        ref={inputRef}
                    />
                    {showErrorMessage && renderErrorMessage()}
                </label>
            </div>
        );
    }
);
